import { useEffect, useState } from "react";
import { createClient } from "@supabase/supabase-js";

const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const COMMAND_CENTER_URL = "https://app.nurturingnurses.net";
const SESSION_CHECK_TIMEOUT_MS = 3000;

// FIRST thing: detect a patient token in the URL. If present, we render the
// app immediately and skip every auth check. Token validation against the
// patient_tokens table happens inside the app, not here.
function detectPatientToken() {
  if (typeof window === "undefined") return false;
  try {
    const search = new URLSearchParams(window.location.search);
    if (search.get("token")) return true;
    const hash = window.location.hash || "";
    const hashQuery = hash.includes("?")
      ? hash.slice(hash.indexOf("?") + 1)
      : hash.replace(/^#/, "");
    if (hashQuery) {
      const hashParams = new URLSearchParams(hashQuery);
      if (hashParams.get("token")) return true;
    }
  } catch (_e) {
    // If URL parsing somehow fails, fall through to staff path.
  }
  return false;
}

const PATIENT_TOKEN_PRESENT = detectPatientToken();

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function redirectToCommandCenter() {
  if (typeof window === "undefined") return;
  window.location.replace(COMMAND_CENTER_URL);
}

export default function AuthGuard({ children }) {
  // Patient flow: render immediately, no checks, no waiting.
  if (PATIENT_TOKEN_PRESENT) {
    return children;
  }

  return <StaffGate>{children}</StaffGate>;
}

function StaffGate({ children }) {
  const [status, setStatus] = useState("checking");

  useEffect(() => {
    let cancelled = false;
    const timeoutId = setTimeout(() => {
      if (!cancelled) redirectToCommandCenter();
    }, SESSION_CHECK_TIMEOUT_MS);

    (async () => {
      try {
        const raw = readCookie("nnhc_session");
        if (!raw) {
          clearTimeout(timeoutId);
          if (!cancelled) redirectToCommandCenter();
          return;
        }

        let parsed;
        try {
          parsed = JSON.parse(raw);
        } catch {
          clearTimeout(timeoutId);
          if (!cancelled) redirectToCommandCenter();
          return;
        }

        const { access_token, refresh_token } = parsed || {};
        if (!access_token || !refresh_token) {
          clearTimeout(timeoutId);
          if (!cancelled) redirectToCommandCenter();
          return;
        }

        const { error } = await supabase.auth.setSession({
          access_token,
          refresh_token,
        });

        clearTimeout(timeoutId);
        if (cancelled) return;

        if (error) {
          redirectToCommandCenter();
          return;
        }

        setStatus("ready");
      } catch (_e) {
        clearTimeout(timeoutId);
        if (!cancelled) redirectToCommandCenter();
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  if (status !== "ready") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "system-ui, sans-serif",
          color: "#666",
        }}
      >
        Authenticating…
      </div>
    );
  }

  return children;
}
